import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/db9n2k3k13ra8mig9610";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

const contactItems = [
  {
    icon: "ri-mail-line",
    label: "邮箱",
    value: "hello@sakura-anime.example",
    hint: "内容合作与投稿建议优先使用邮箱",
  },
  {
    icon: "ri-phone-line",
    label: "联系电话",
    value: "400-800-2026",
    hint: "工作日 10:00 - 19:00 有人接听",
  },
  {
    icon: "ri-map-pin-line",
    label: "办公地址",
    value: "上海市徐汇区漕溪北路 88 号 12 层",
    hint: "来访请提前一天预约",
  },
  {
    icon: "ri-time-line",
    label: "响应时间",
    value: "1 - 2 个工作日",
    hint: "周末的留言会顺延到周一处理",
  },
];

export default function Contact() {
  useSeo({
    title: "联系我们 - 樱花动漫官网",
    description:
      "联系樱花动漫官网编辑部，提交内容纠错、合作洽谈或使用建议。也可以通过页面表单直接留言，我们会在 1-2 个工作日内回复。",
    keywords: "联系樱花动漫,动漫平台合作,意见反馈,樱花动漫官网",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = typeof value === "string" ? value : value.name;
      if (text.trim() !== "") payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        formRef.current?.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重试。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-11 md:px-8 md:py-14">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">联系我们</span>
            </nav>
            <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              无论是内容纠错、作品推荐，还是合作与投稿，都欢迎直接留言。我们看得比较慢，但每一条都会看。
            </p>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <h2 className="text-lg font-bold text-foreground-950">联系方式</h2>
              <p className="mt-1.5 text-xs text-foreground-500">工作日优先处理，节假日可能略有延迟。</p>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {contactItems.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <p className="mt-3 text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-1 break-words text-sm font-medium text-foreground-900">{item.value}</p>
                    <p className="mt-1.5 text-[11px] leading-relaxed text-foreground-500">{item.hint}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
                <h3 className="text-sm font-semibold text-foreground-950">关注我们</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  每天更新新番提醒与编辑推荐，也欢迎在评论区和我们讨论剧情。
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {[
                    { icon: "ri-weibo-fill", label: "微博" },
                    { icon: "ri-wechat-fill", label: "微信" },
                    { icon: "ri-bilibili-fill", label: "哔哩哔哩" },
                    { icon: "ri-mail-line", label: "邮件" },
                  ].map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      <i className={`${item.icon} text-sm`} />
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 rounded-lg bg-secondary-100 p-4">
                <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary-900">
                  <i className="ri-information-line mt-0.5 text-sm" />
                  本站为演示项目，不提供真实视频播放服务。所有作品资料、资讯内容与联系信息仅用于产品形态展示。
                </p>
              </div>
            </div>

            <div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                <h2 className="text-lg font-bold text-foreground-950">留下你的想法</h2>
                <p className="mt-1.5 text-xs text-foreground-500">
                  带 <span className="text-primary-600">*</span> 的为必填项，留言内容请控制在 500 字以内。
                </p>

                {status === "success" ? (
                  <div className="mt-6 flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-12 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-check-line text-xl" />
                    </span>
                    <h3 className="mt-4 text-base font-bold text-foreground-950">留言已收到，谢谢你</h3>
                    <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                      我们已经收到了你的留言，会在 1-2 个工作日内查看。如果还想补充什么，也可以再写一条。
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm text-primary-700 transition-colors hover:bg-primary-100"
                    >
                      再写一条
                    </button>
                  </div>
                ) : (
                  <form ref={formRef} id="contact-form" data-readdy-form onSubmit={handleSubmit} className="mt-5 space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="contact-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
                          姓名 <span className="text-primary-600">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          maxLength={40}
                          placeholder="怎么称呼你"
                          className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                          邮箱 <span className="text-primary-600">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="用于接收回复"
                          className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-medium text-foreground-700">
                        留言类型
                      </label>
                      <select
                        id="contact-subject"
                        name="subject"
                        defaultValue="内容建议"
                        className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                      >
                        <option value="内容建议">内容建议</option>
                        <option value="内容纠错">内容纠错</option>
                        <option value="合作洽谈">合作洽谈</option>
                        <option value="投稿">投稿</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="mb-1.5 block text-xs font-medium text-foreground-700">
                        留言内容 <span className="text-primary-600">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={6}
                        placeholder="想推荐的番、发现的错误，或者任何建议……"
                        className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                      />
                      <p className="mt-1 text-[11px] text-foreground-400">最多 500 字</p>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      className="site_mail_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    {status === "error" && formError && (
                      <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-800">
                        <i className="ri-error-warning-line mt-0.5 text-sm" />
                        <span>{formError}</span>
                      </div>
                    )}

                    <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-[11px] leading-relaxed text-foreground-500">
                        提交即表示同意我们在回复范围内使用你填写的信息。
                      </p>
                      <button
                        type="submit"
                        disabled={status === "submitting"}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {status === "submitting" ? "提交中…" : "提交留言"}
                        {status !== "submitting" && <i className="ri-send-plane-line text-sm" />}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Link
                  to="/about"
                  className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-100 px-4 py-3.5 transition-colors hover:border-primary-200"
                >
                  <span>
                    <span className="block text-sm text-foreground-900">了解我们的团队</span>
                    <span className="mt-0.5 block text-[11px] text-foreground-500">四个人，和一堆没看完的番</span>
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400 transition-colors group-hover:text-primary-600" />
                </Link>
                <Link
                  to="/news"
                  className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-100 px-4 py-3.5 transition-colors hover:border-primary-200"
                >
                  <span>
                    <span className="block text-sm text-foreground-900">看看最新资讯</span>
                    <span className="mt-0.5 block text-[11px] text-foreground-500">每天 09:00 与 18:00 更新</span>
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400 transition-colors group-hover:text-primary-600" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}